import type {SidebarChat} from '../types/sidebar-chat'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {getAgentDisplayName} from '@github-ui/copilot-shared-ui/agents/agent-info-registry'
import {COPILOT_AGENT_SLUG, COPILOT_CMC_SLUG} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {SessionState} from '../types/session'
import {
  CLOUD_TYPE_ENVIRONMENT_KINDS,
  FILTER_STATUS_CANCELLED,
  FILTER_STATUS_COMPLETED,
  FILTER_STATUS_FAILED,
  FILTER_STATUS_IDLE,
  FILTER_STATUS_IN_PROGRESS,
  FILTER_STATUS_NEEDS_ATTENTION,
  FILTER_STATUS_QUEUED,
  FILTER_STATUS_TIMED_OUT,
  toDisplaySlug,
  VALID_AGENT_TYPES,
  type EffectiveSidebarFilters,
} from './filter-utils'
import type {SidebarRepository} from '../types/sidebar-repository'

const CHAT_STATE_TO_FILTER_STATUS: Record<string, string> = {
  running: FILTER_STATUS_IN_PROGRESS,
  [SessionState.InProgress]: FILTER_STATUS_IN_PROGRESS,
  [SessionState.Queued]: FILTER_STATUS_QUEUED,
  [SessionState.Idle]: FILTER_STATUS_IDLE,
  stopped: FILTER_STATUS_IDLE,
  [SessionState.WaitingForUser]: FILTER_STATUS_NEEDS_ATTENTION,
  [SessionState.Failed]: FILTER_STATUS_FAILED,
  [SessionState.Completed]: FILTER_STATUS_COMPLETED,
  [SessionState.Cancelled]: FILTER_STATUS_CANCELLED,
  [SessionState.TimedOut]: FILTER_STATUS_TIMED_OUT,
}

export function getSidebarChatFilterStatus(state: string | undefined): string | undefined {
  return state ? CHAT_STATE_TO_FILTER_STATUS[state] : undefined
}

export function matchesSidebarChatFilters(
  chat: SidebarChat,
  filters: EffectiveSidebarFilters,
  repository?: SidebarRepository,
  filterableAgents?: AgentInfo[],
): boolean {
  if (filters.repo) {
    if (!repository) return false
    if (
      chat.legacy ? chat.repoId !== repository.id : chat.nwo?.toLowerCase() !== repository.nameWithOwner.toLowerCase()
    )
      return false
  }
  if (filters.agents.length > 0) {
    if (chat.legacy) return false
    if (!matchesAgentFacet(chat, filters.agents, filterableAgents)) return false
  }
  if (filters.types.length > 0) {
    if (chat.legacy) return false
    if (!matchesTypeFacet(chat, filters.types, filterableAgents)) return false
  }
  if (filters.statuses.length === 0) return true
  const status = chat.legacy ? undefined : getSidebarChatFilterStatus(chat.latestSessionState)
  return status !== undefined && filters.statuses.includes(status)
}

function matchesAgentFacet(
  chat: SidebarChat,
  selectedAgents: readonly string[],
  filterableAgents?: AgentInfo[],
): boolean {
  const customNames = selectedAgents
    .filter(agent => agent.startsWith('custom:'))
    .map(agent => agent.slice('custom:'.length))
    .filter(agent => !agent.includes(','))
  if (chat.customAgentName && customNames.includes(chat.customAgentName)) {
    return true
  }
  const builtinAgents = selectedAgents.filter(agent => !agent.startsWith('custom:'))
  if (builtinAgents.length === 0 || !chat.agentSlug) return false
  const agent = findFilterableAgentForChatSlug(chat.agentSlug, filterableAgents)
  if (!agent) return false
  return builtinAgents.includes(toDisplaySlug(getAgentDisplayName(agent)))
}

function findFilterableAgentForChatSlug(agentSlug: string, filterableAgents?: AgentInfo[]): AgentInfo | undefined {
  const filterableAgentSlug = agentSlug === COPILOT_CMC_SLUG ? COPILOT_AGENT_SLUG : agentSlug
  return filterableAgents?.find(candidate => candidate.slug === filterableAgentSlug)
}

function matchesTypeFacet(
  chat: SidebarChat,
  selectedTypes: readonly string[],
  filterableAgents?: AgentInfo[],
): boolean {
  const validTypes = selectedTypes.filter(type => VALID_AGENT_TYPES.has(type))
  if (
    chat.environment?.kind &&
    validTypes.includes('cloud') &&
    CLOUD_TYPE_ENVIRONMENT_KINDS.some(kind => kind === chat.environment?.kind)
  ) {
    return true
  }
  if (!chat.agentSlug) return false
  const agent = findFilterableAgentForChatSlug(chat.agentSlug, filterableAgents)
  if (!agent) return false
  return validTypes.includes(agent.group ?? 'cloud')
}
